import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext.jsx';
import { useWorkspace } from '../context/WorkspaceContext.jsx';
import { Badge, Empty, Pagination, STATUS_TONE, fmtDate, label } from '../components/ui.jsx';
import { t } from '../i18n/index.jsx';
import ProjectForm, { PROJECT_STATUSES, COUNTRIES, blankProject } from '../components/forms/ProjectForm.jsx';
import { IconPlus } from '../components/icons.jsx';
import DataTable from '../components/DataTable.jsx';
import ExportButton from '../components/ExportButton.jsx';
import { cachedFetch, invalidate } from '../api/cache.js';

const nameTemplate = (row) => (
  <Link to={`/projects/${row.id}`} className="strong">{row.name}</Link>
);

const statusTemplate = (row) => <Badge tone={STATUS_TONE[row.status]}>{label(row.status)}</Badge>;

const commissionTemplate = (row) => (
  row.commission?.amount ? `€${Number(row.commission.amount).toLocaleString()}` : '—'
);

export default function Projects() {
  const { user } = useAuth();
  const { isGeneral } = useWorkspace();
  const navigate = useNavigate();
  const canEdit = user.role === 'admin' || user.role === 'manager';
  // /projects is shared between Trade and General; the Funds workspace has no
  // Projects nav entry, so anything that isn't General is treated as Trade —
  // never a mixed list.
  const workspaceParam = isGeneral ? 'general' : 'trade';
  const [searchParams, setSearchParams] = useSearchParams();
  const [list, setList] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [meta, setMeta] = useState(null);
  const [editing, setEditing] = useState(null);

  const status = searchParams.get('status') || '';
  const country = searchParams.get('country') || '';
  const search = searchParams.get('q') || '';
  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);

  const patchParams = useCallback((patch) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(patch).forEach(([k, v]) => {
        if (v == null || v === '' || v === false) next.delete(k);
        else next.set(k, String(v));
      });
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (country) params.set('country', country);
    if (search) params.set('search', search);
    params.set('workspace', workspaceParam);
    params.set('page', String(page));
    params.set('per_page', '20');
    const path = `/projects?${params}`;
    const res = await cachedFetch(path, () => api.get(path));
    setList(res.data);
    setMeta(res.meta || null);
  }, [status, country, search, page, workspaceParam]);

  useEffect(() => {
    const id = setTimeout(load, 300);
    return () => clearTimeout(id);
  }, [load]);

  useEffect(() => {
    cachedFetch('/companies', () => api.get('/companies'))
      .then((r) => setCompanies(r.data))
      .catch(() => setCompanies([]));
  }, []);

  // Internal initiatives have no manufacturers, clients or commission —
  // showing those columns would be four empty cells on every row.
  const columns = isGeneral ? [
    { headerText: t('Initiative'), width: 220, template: nameTemplate },
    { field: 'country', headerText: t('Country'), width: 110 },
    { field: 'category_name', headerText: t('Type'), width: 120 },
    { headerText: t('Status'), width: 110, template: statusTemplate },
    { field: 'deadline', headerText: t('Deadline'), width: 120, template: (r) => fmtDate(r.deadline) },
  ] : [
    { headerText: t('Project'), width: 180, template: nameTemplate },
    { field: 'manufacturer_names', headerText: t('Manufacturers'), width: 160 },
    { field: 'client_names', headerText: t('Clients'), width: 160 },
    { field: 'category_name', headerText: t('Type'), width: 120 },
    { headerText: t('Status'), width: 110, template: statusTemplate },
    { headerText: t('Commission'), width: 120, template: commissionTemplate },
    { field: 'deadline', headerText: t('Deadline'), width: 120, template: (r) => fmtDate(r.deadline) },
  ];

  const hasFilters = !!(status || country || search);

  return (
    <div className="page fade-in">
      <div className="page-head">
        <h1 className="page-title">{isGeneral ? t('Initiatives') : t('Projects')}</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <ExportButton
            filename={isGeneral ? 'initiatives' : 'projects'}
            rows={list || []}
            columns={[
              { header: t('Project'), field: 'name' },
              { header: t('Manufacturers'), field: 'manufacturer_names' },
              { header: t('Clients'), field: 'client_names' },
              { header: t('Type'), field: 'category_name' },
              { header: t('Status'), field: 'status' },
              { header: t('Deadline'), field: 'deadline' },
            ]}
            disabled={!list?.length}
          />
          {canEdit && (
            <button
              type="button"
              className="btn primary"
              onClick={() => setEditing({ ...blankProject, business_line: isGeneral ? 'internal' : 'trade' })}
            >
              <IconPlus width={15} height={15} /> {isGeneral ? t('New initiative') : t('New project')}
            </button>
          )}
        </div>
      </div>

      <div className="toolbar">
        <form
          onSubmit={(e) => { e.preventDefault(); load(); }}
          className="search"
        >
          <input
            placeholder={t('Search project name…')}
            value={search}
            onChange={(e) => patchParams({ q: e.target.value || null, page: null })}
          />
        </form>
        <select
          value={status}
          onChange={(e) => patchParams({ status: e.target.value || null, page: null })}
        >
          <option value="">{t('All statuses')}</option>
          {PROJECT_STATUSES.map((s) => <option key={s} value={s}>{label(s)}</option>)}
        </select>
        <select
          value={country}
          onChange={(e) => patchParams({ country: e.target.value || null, page: null })}
        >
          <option value="">{t('All countries')}</option>
          {COUNTRIES.map((c) => <option key={c} value={c}>{t(c)}</option>)}
        </select>
        {hasFilters && (
          <button
            type="button"
            className="btn ghost sm"
            onClick={() => patchParams({ status: null, country: null, q: null, page: null })}
          >
            {t('Clear')}
          </button>
        )}
      </div>

      {!list ? (
        <DataTable data={[]} columns={columns} loading />
      ) : list.length === 0 ? (
        <Empty>{isGeneral ? t('No initiatives yet.') : t('No projects yet.')}</Empty>
      ) : (
        <DataTable
          data={list}
          columns={columns}
          onRowClick={(row) => navigate(`/projects/${row.id}`)}
          onRowAction={(row) => navigate(`/projects/${row.id}`)}
        />
      )}

      <Pagination meta={meta} onPage={(p) => patchParams({ page: p <= 1 ? null : String(p) })} />

      {editing && (
        <ProjectForm
          initial={editing}
          companies={companies}
          isAdmin={user.role === 'admin'}
          onClose={() => setEditing(null)}
          onSaved={() => { invalidate('/projects'); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}
